import React, { CSSProperties, useEffect } from 'react'
import makeStyles from '@material-ui/core/styles/makeStyles'
import styles from '../../../styles'
import { FormattedMessage, IntlShape, useIntl } from 'react-intl'
import { useMediaQuery } from 'react-responsive'
import classNames from 'classnames'
import { IconButton, InputBase, useTheme } from '@material-ui/core'
import Icon from '../../../components/icon/Icon'
import FaqHeaderSubmenu from './FaqHeaderSubmenu'
import { ModelCategory } from '../../../types/faq/faq_model'
import iconsEnum from '../../../enums/iconsEnum'
import Sticky from 'react-sticky-el'

const useStyle = makeStyles((theme) => ({
		...styles(theme),
		faqStyle: {
			width: '100%',
			display: 'flex',
			alignItems: 'center',
			flexDirection: 'column',
			justifyContent: 'center',
			backgroundSize: 'cover',
			boxSizing: 'border-box'
		},
		faqBackgroundStyleLargeScreen: {
			backgroundPositionX: 'right'
		},
		faqBackgroundStyleSmallScreen: {
			backgroundPositionX: 'left'
		},
		faqHeaderTextLargeScreen: {
			textAlign: 'center',
			width: '54%',
			marginBottom: '32px'
		},
		faqHeaderTextSmallScreen: {
			textAlign: 'center',
			width: '64%',
			marginBottom: '16px'
		},
		faqImageLargeScreenStyle: {
			backgroundImage: `url('img/faq_img/${process.env.REACT_APP_TARGET}/bandeau.jpg')`,
			height: '347px'
		},
		faqImageSmallScreenStyle: {
			backgroundImage: `url('img/faq_img/${process.env.REACT_APP_TARGET}/small_bandeau.jpg')`,
			height: '247px'
		},
		faqSearchFieldLargeScreenStyle: {
			width: '60%',
			height: '70px'
		},
		faqSearchFieldSmallScreenStyle: {
			width: '100%',
			height: '52px'
		},
		searchField: {
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'space-between',
			borderRadius: '54px',
			backgroundColor: 'white'
		},
		faqInputBaseLargeScreenStyle: {
			marginLeft: '40px'
		},
		faqInputBaseSmallScreenStyle: {
			marginLeft: '20px'
		},
		inputInputBase: {
			position: 'relative',
			width: '100%',
			color: theme.palette.blue.main
		},
		faqIconSearchSmallScreenStyle: {
			width: '40px',
			height: '40px',
			marginRight: '10px'
		},
		faqIconSearchLargeScreenStyle: {
			width: '49px',
			height: '49px',
			marginRight: '12px'
		},
		iconSearch: {
			display: 'flex',
			fill: '#fff',
			backgroundColor: theme.palette.dynamic.main,
			'&:hover': {
				backgroundColor: theme.palette.dynamic['50'],
				boxShadow: 'none',
				fill: theme.palette.blue.main
			}
		}
	}
))

interface AcceptingProps {
	setInputSearch: Function,
	categories: Array<ModelCategory>,
	isEmpty: (cat: Array<ModelCategory>) => boolean
}

type FaqHeaderProps = AcceptingProps

const FaqHeader: React.FC<FaqHeaderProps> = (
	{
		setInputSearch, categories, isEmpty
	}
) => {

	const classes = useStyle()
	const intl: IntlShape = useIntl()
	const theme = useTheme()
	const largePlaceholder: string = intl.formatMessage({ id: 'faq.placeholder' })
	const smallPlaceHolder: string = intl.formatMessage({ id: 'faq.smallPlaceholder' })
	const [input, setInput] = React.useState('')
	const [isFixed, setIsFixed] = React.useState(false)

	const isLargeScreen = useMediaQuery(
		{
			query: '(min-width: 1025px)'
		}
	)

	useEffect(() => {
		if (isLargeScreen) {
			setIsFixed(false)
		}
	}, [isLargeScreen])

	const stickySearchField: CSSProperties = {
		top: '65px',
		borderBottom: `2px solid ${theme.palette.blue['50']}`,
		width: '95%',
		zIndex: 2
	}

	const faqClass = classNames(
		isLargeScreen ? [classes.faqImageLargeScreenStyle] : [classes.faqImageSmallScreenStyle],
		isLargeScreen ? [classes.faqBackgroundStyleLargeScreen] : [classes.faqBackgroundStyleSmallScreen],
		classes.faqStyle
	)
	const titleClass = classNames(
		isLargeScreen ? [classes.title1] : [classes.title2],
		isLargeScreen ? [classes.faqHeaderTextLargeScreen] : [classes.faqHeaderTextSmallScreen],
		classes.colorBlue
	)
	const searchFieldClass = classNames(
		classes.searchField,
		isLargeScreen ? [classes.faqSearchFieldLargeScreenStyle] : [classes.faqSearchFieldSmallScreenStyle]
	)

	const inputFieldClass = classNames(
		isLargeScreen ? [classes.faqInputBaseLargeScreenStyle] : [classes.faqInputBaseSmallScreenStyle],
		classes.inputInputBase,
		classes.colorBlue
	)

	const inputPlaceholder = classNames(
		isLargeScreen ? [classes.text1] : [classes.text3],
		classes.colorBlue,
		classes.semiBold
	)

	const iconSearch = classNames(
		classes.iconSearch,
		isLargeScreen ? [classes.faqIconSearchLargeScreenStyle] : [classes.faqIconSearchSmallScreenStyle]
	)

	const searchFieldSize = classNames(
		isFixed ? [classes.faqSearchFieldSmallScreenStyle] : [classes.faqSearchFieldLargeScreenStyle]
	)

	const handleChangeInput = (event: React.ChangeEvent<HTMLInputElement>) => {
		const textInput = event.target.value
		setInput(textInput)
		if (textInput.length >= 3 || textInput.length === 0) {
			setInputSearch(textInput)
		}
	}

	const searchClick = () => {
		setInputSearch(input)
	}

	const handleEnter = (event: React.KeyboardEvent<HTMLDivElement>) => {
		if (event.key === 'Enter') {
			setInputSearch(input)
		}
	}

	const onFixedToggle = (fixed: boolean) => {
		setIsFixed(fixed)
	}

	return (
		<div>
			<div className={faqClass}>
                <span
	                className={titleClass}
                >
                    <FormattedMessage
	                    id="faq.faq_title"
	                    defaultMessage="Comment pouvons-nous vous aider ?"
	                    description="Faq header title text"
                    />
                </span>
				{isLargeScreen ?
					<div className={searchFieldClass}>
						<InputBase
							onKeyPress={handleEnter}
							onChange={handleChangeInput}
							placeholder={largePlaceholder}
							classes={{
								root: inputFieldClass,
								input: inputPlaceholder
							}}
						/>
						<IconButton onClick={searchClick} className={iconSearch}>
							<Icon size="100%" icon={iconsEnum.Search2} />
						</IconButton>
					</div>
					:
					<Sticky boundaryElement="#Faq"
					        onFixedToggle={onFixedToggle}
					        stickyStyle={stickySearchField}
					        className={searchFieldSize}
					>
						<div className={searchFieldClass}>
							<InputBase
								onKeyPress={handleEnter}
								onChange={handleChangeInput}
								placeholder={smallPlaceHolder}
								classes={{
									root: inputFieldClass,
									input: inputPlaceholder
								}}
							/>
							<IconButton onClick={searchClick} className={iconSearch}>
								<Icon size="100%" icon={iconsEnum.Search2} />
							</IconButton>
						</div>
					</Sticky>
				}
			</div>
			{isLargeScreen && !isEmpty(categories) ?
				<FaqHeaderSubmenu
					categories={categories}
				/>
				:
				null
			}
		</div>
	)
}

export default FaqHeader
